import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  message as antMessage,
  Progress,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";

import {
  ArrowRightOutlined,
  CalendarOutlined,
  FireOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import DashboardLayout from "../components/DashboardLayout";
import PageLoader from "../components/PageLoader";
import avatarImage from "../assets/spr_placeholder_avatar.png";
import profileIcon from "../assets/profile.png";
import NewsCarousel from "../components/NewsCarousel";
import useCurrentUserData from "../hooks/useCurrentUserData";
import useRewardsCatalog from "../hooks/useRewardsCatalog";
import RewardedProfileDisplay from "../components/RewardedProfileDisplay";
import { useAuth } from "../contexts/AuthContext";
import { UpdateEntryLevels } from "../firebase-config";
import { trackLevelUp } from "../analytics";
import EventCalendar from "../components/EventCalendar";
import { getLevelFromExp, getExpProgress } from "../utils/levels";
import {
  AVERAGE_SKILL_HELP,
  getAverageSkillPercent,
  getBestSkill,
  getSkillDisplayName,
} from "../utils/skillUtils";
import { mergeRewardsWithUserState } from "../utils/rewards";
import { dashboardPalette } from "../theme";
import { usePublicProfiles } from "../hooks/useChat";
import useFriends from "../hooks/useFriends";
import { AddFriend, MAX_FRIENDS } from "../firebase-friends";

const Dashboard: React.FC = () => {
  const { userData, isLoading: userLoading } = useCurrentUserData();
  const { catalog, isLoading: catalogLoading } = useRewardsCatalog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const userSkills = userData?.Skill ?? [];
  const mergedRewards = useMemo(
    () => mergeRewardsWithUserState(catalog, userData?.Recompensas),
    [catalog, userData?.Recompensas],
  );

  const formatPercent = (value: number) => value.toFixed(2);

  const currentExp = userData?.CurrentExp ?? 0;
  const derivedLevel = getLevelFromExp(currentExp);

  const levelSyncRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !userData) return;
    if (userData.Nivel === derivedLevel) return;
    const syncKey = `${user.uid}:${derivedLevel}`;
    if (levelSyncRef.current === syncKey) return;
    levelSyncRef.current = syncKey;
    void UpdateEntryLevels(user.uid, derivedLevel);
    if (derivedLevel > (userData.Nivel ?? 1)) {
      trackLevelUp(derivedLevel);
    }
  }, [user, userData, derivedLevel]);

  const currentExpProgress = userData
    ? getExpProgress(derivedLevel, currentExp)
    : 0;

  const topSkill = getBestSkill(userSkills);
  const avgSkill = getAverageSkillPercent(userSkills);

  const myUid = user?.uid ?? "";
  const { profiles } = usePublicProfiles();
  const { friendIds, friendSet } = useFriends(myUid);
  const [addingId, setAddingId] = useState<string | null>(null);

  const quickAddCandidates = useMemo(
    () =>
      profiles
        .filter((p) => p.id !== myUid && p.Nivel > 0 && !friendSet.has(p.id))
        .slice(0, 4),
    [profiles, myUid, friendSet],
  );

  const handleQuickAdd = async (profileId: string, nombre: string) => {
    if (friendIds.length >= MAX_FRIENDS) {
      antMessage.warning(`Podés tener hasta ${MAX_FRIENDS} amigos.`);
      return;
    }
    setAddingId(profileId);
    try {
      await AddFriend(myUid, profileId);
      antMessage.success(`${nombre} ahora está en tus amigos.`);
    } catch (error) {
      console.error(error);
      antMessage.error("No se pudo agregar. Probá de nuevo.");
    } finally {
      setAddingId(null);
    }
  };

  const sectionCardStyle: React.CSSProperties = {
    borderRadius: 24,
    border: `1px solid ${dashboardPalette.panelBorder}`,
    boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
    overflow: "hidden",
  };

  if ((userLoading && !userData) || (catalogLoading && !catalog.length)) {
    return (
      <DashboardLayout showNavbar>
        <PageLoader minHeight={480} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout showNavbar>
      <div style={{ maxWidth: 1440, margin: "0 auto", width: "100%" }}>
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} lg={6} style={{ display: "flex" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <Card
                className="app-glass"
                style={sectionCardStyle}
                bodyStyle={{ padding: 18 }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Typography.Text
                    strong
                    style={{
                      color: dashboardPalette.headerBlue,
                      letterSpacing: 1.1,
                      textTransform: "uppercase",
                    }}
                  >
                    Panel rápido
                  </Typography.Text>
                  <Typography.Title
                    level={3}
                    style={{ margin: 0, color: dashboardPalette.headerBlue }}
                  >
                    Tu recorrido
                  </Typography.Title>
                  <Typography.Paragraph
                    style={{ margin: 0, color: dashboardPalette.textDark }}
                  >
                    Entrá a las áreas principales con un solo click.
                  </Typography.Paragraph>

                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Button
                      block
                      type="primary"
                      icon={<ArrowRightOutlined />}
                      onClick={() => navigate("/skills")}
                      style={{
                        borderRadius: 14,
                        fontWeight: 800,
                        background: "var(--dashboard-yellow)",
                        borderColor: "var(--dashboard-yellow-dark)",
                        color: "var(--dashboard-header-blue)",
                      }}
                    >
                      Ver skills
                    </Button>
                    <Button
                      block
                      icon={<UserOutlined />}
                      onClick={() => navigate("/profile")}
                      style={{ borderRadius: 14, fontWeight: 800 }}
                    >
                      Abrir perfil
                    </Button>
                    <Button
                      block
                      icon={<CalendarOutlined />}
                      onClick={() => navigate("/redeem")}
                      style={{ borderRadius: 14, fontWeight: 800 }}
                    >
                      Recompensas
                    </Button>
                  </Space>
                </Space>
              </Card>

              <Card
                className="app-glass"
                style={{
                  ...sectionCardStyle,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
                bodyStyle={{
                  padding: 18,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <Typography.Text
                  strong
                  style={{
                    color: dashboardPalette.headerBlue,
                    textTransform: "uppercase",
                  }}
                >
                  Comunidad
                </Typography.Text>
                <Typography.Title
                  level={5}
                  style={{ margin: 0, color: dashboardPalette.headerBlue }}
                >
                  Sumá compañeros
                </Typography.Title>

                {quickAddCandidates.length ? (
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    {quickAddCandidates.map((p) => (
                      <div
                        key={p.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto auto",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 10px",
                          borderRadius: 14,
                          background: "rgba(255,255,255,0.72)",
                          border: `1px solid ${dashboardPalette.panelBorder}`,
                        }}
                      >
                        <Space size={8} style={{ minWidth: 0 }}>
                          <Avatar
                            size={28}
                            src={p.PortraitImg || profileIcon}
                            style={{ background: "#fff4c4", flexShrink: 0 }}
                          />
                          <Typography.Text
                            ellipsis={{ tooltip: p.Nombre }}
                            style={{
                              color: dashboardPalette.textDark,
                              maxWidth: 120,
                            }}
                          >
                            {p.Nombre}
                          </Typography.Text>
                        </Space>
                        <Typography.Text
                          style={{
                            fontSize: 12,
                            color: dashboardPalette.textDark,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Nv. {p.Nivel}
                        </Typography.Text>
                        <Button
                          size="small"
                          type="primary"
                          icon={<UserAddOutlined />}
                          loading={addingId === p.id}
                          onClick={() => void handleQuickAdd(p.id, p.Nombre)}
                          style={{
                            borderRadius: 10,
                            fontWeight: 700,
                            background: "var(--dashboard-yellow)",
                            borderColor: "var(--dashboard-yellow-dark)",
                            color: "var(--dashboard-header-blue)",
                          }}
                        />
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text style={{ color: dashboardPalette.textDark }}>
                    No hay más compañeros para sumar por ahora.
                  </Typography.Text>
                )}

                <Button
                  block
                  icon={<TeamOutlined />}
                  onClick={() => navigate("/comunidad")}
                  style={{
                    borderRadius: 14,
                    fontWeight: 800,
                    marginTop: "auto",
                  }}
                >
                  Ver comunidad
                </Button>
              </Card>
            </div>
          </Col>

          <Col xs={24} lg={10} style={{ display: "flex" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <Card
                className="app-glass hero-panel"
                style={sectionCardStyle}
                bodyStyle={{ padding: 0, overflow: "hidden" }}
              >
                <div
                  style={{
                    padding: 20,
                    background:
                      "linear-gradient(135deg, rgba(47,43,116,0.94), rgba(124,92,252,0.9))",
                  }}
                >
                  <Typography.Title
                    level={3}
                    style={{ margin: 0, color: "#fff" }}
                  >
                    Noticias
                  </Typography.Title>
                  <Typography.Text style={{ color: "rgba(255,255,255,0.88)" }}>
                    Novedades y anuncios importantes.
                  </Typography.Text>
                </div>
                <NewsCarousel />
              </Card>

              <Card
                className="app-glass"
                style={{
                  ...sectionCardStyle,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
                bodyStyle={{
                  padding: 0,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: 18,
                    borderBottom: `1px solid ${dashboardPalette.panelBorder}`,
                    background: "rgba(255,255,255,0.55)",
                  }}
                >
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, color: dashboardPalette.headerBlue }}
                  >
                    Calendario
                  </Typography.Title>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <EventCalendar />
                </div>
              </Card>
            </div>
          </Col>

          <Col xs={24} lg={8} style={{ display: "flex" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <Card
                className="app-glass"
                style={sectionCardStyle}
                bodyStyle={{ padding: 18 }}
                onClick={() => navigate("/profile")}
              >
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <Space align="center" size={14}>
                      <RewardedProfileDisplay
                        src={userData?.PortraitImg}
                        alt="Perfil"
                        fallbackSrc={avatarImage}
                        width={92}
                        height={92}
                        border={`3px solid ${dashboardPalette.headerBlue}`}
                        boxShadow="0 6px 0 rgba(47,43,116,0.18)"
                        backgroundColor="#fff4c4"
                        rewards={[...mergedRewards, ...(userData?.Items ?? [])]}
                        overlayMode="full"
                        overlayImageSx={{ top: -12, left: 0 }}
                        badgeSize={22}
                        overlaySize={28}
                      />
                      <div>
                        <Typography.Text
                          strong
                          style={{
                            display: "block",
                            color: dashboardPalette.headerBlue,
                            fontSize: 18,
                          }}
                        >
                          {userData?.Nombre || "Usuario"}
                        </Typography.Text>
                        <Typography.Text
                          style={{ color: dashboardPalette.textDark }}
                        >
                          Nivel {derivedLevel}
                        </Typography.Text>
                      </div>
                    </Space>

                    <Button
                      type="primary"
                      icon={<UserOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate("/profile");
                      }}
                      style={{
                        borderRadius: 14,
                        background: "var(--dashboard-yellow)",
                        borderColor: "var(--dashboard-yellow-dark)",
                        color: "var(--dashboard-header-blue)",
                        fontWeight: 800,
                      }}
                    >
                      Ver perfil
                    </Button>
                  </div>

                  <div
                    style={{
                      borderRadius: 18,
                      padding: 16,
                      background:
                        "linear-gradient(135deg, rgba(47,43,116,0.06), rgba(79,194,134,0.08))",
                    }}
                  >
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: "100%" }}
                    >
                      <Typography.Text
                        strong
                        style={{
                          color: dashboardPalette.headerBlue,
                          textTransform: "uppercase",
                        }}
                      >
                        Progreso general
                      </Typography.Text>
                      <Progress
                        percent={currentExpProgress}
                        strokeColor="var(--dashboard-teal)"
                        trailColor="var(--dashboard-track)"
                        format={(percent) =>
                          `${Number(percent ?? 0).toFixed(2)}%`
                        }
                      />
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        {formatPercent(currentExpProgress)}% hacia el siguiente
                        nivel
                      </Typography.Text>
                    </Space>
                  </div>
                </Space>
              </Card>

              <Card
                className="app-glass"
                style={{ ...sectionCardStyle, flex: 1 }}
                bodyStyle={{ padding: 18 }}
              >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Space direction="vertical" size={0}>
                    <Typography.Text
                      strong
                      style={{
                        color: dashboardPalette.headerBlue,
                        textTransform: "uppercase",
                      }}
                    >
                      Resumen
                    </Typography.Text>
                    <Typography.Title
                      level={3}
                      style={{ margin: 0, color: dashboardPalette.headerBlue }}
                    >
                      Vista rápida
                    </Typography.Title>
                  </Space>

                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <div
                        style={{
                          borderRadius: 18,
                          padding: 14,
                          background: "rgba(255,255,255,0.75)",
                          border: `1px solid ${dashboardPalette.panelBorder}`,
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{
                            display: "block",
                            color: dashboardPalette.headerBlue,
                          }}
                        >
                          Skills
                        </Typography.Text>
                        <Typography.Title
                          level={3}
                          style={{ margin: 0, color: dashboardPalette.teal }}
                        >
                          {userSkills.length}
                        </Typography.Title>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div
                        style={{
                          borderRadius: 18,
                          padding: 14,
                          background: "rgba(255,255,255,0.75)",
                          border: `1px solid ${dashboardPalette.panelBorder}`,
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{
                            display: "block",
                            color: dashboardPalette.headerBlue,
                          }}
                        >
                          Promedio de skills{" "}
                          <Tooltip title={AVERAGE_SKILL_HELP}>
                            <InfoCircleOutlined style={{ fontSize: 12 }} />
                          </Tooltip>
                        </Typography.Text>
                        <Typography.Title
                          level={3}
                          style={{ margin: 0, color: "#ff6ddd" }}
                        >
                          {avgSkill}%
                        </Typography.Title>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div
                        style={{
                          borderRadius: 18,
                          padding: 14,
                          background: "rgba(255,255,255,0.75)",
                          border: `1px solid ${dashboardPalette.panelBorder}`,
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{
                            display: "block",
                            color: dashboardPalette.headerBlue,
                          }}
                        >
                          Mejor skill
                        </Typography.Text>
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            color: dashboardPalette.headerBlue,
                          }}
                        >
                          {topSkill
                            ? getSkillDisplayName(topSkill.Materia)
                            : "Sin datos"}
                        </Typography.Title>
                      </div>
                    </Col>
                  </Row>

                  <Button
                    block
                    icon={<FireOutlined />}
                    onClick={() => navigate("/redeem")}
                    style={{ borderRadius: 14, fontWeight: 800 }}
                  >
                    Ver recompensas
                  </Button>
                </Space>
              </Card>

              <Card
                className="app-glass"
                style={sectionCardStyle}
                bodyStyle={{ padding: 18 }}
                onClick={() => navigate("/skills")}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Typography.Text
                    strong
                    style={{
                      color: dashboardPalette.headerBlue,
                      textTransform: "uppercase",
                    }}
                  >
                    Skills
                  </Typography.Text>
                  {userSkills.slice(0, 3).map((skill, index) => (
                    <div key={index}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 6,
                        }}
                      >
                        <Typography.Text
                          strong
                          style={{ color: dashboardPalette.textDark }}
                        >
                          {getSkillDisplayName(skill.Materia)}
                        </Typography.Text>
                        <Typography.Text
                          strong
                          style={{ color: dashboardPalette.textDark }}
                        >
                          {Math.min(100, (skill.Progress / 6667) * 100).toFixed(
                            2,
                          )}
                          %
                        </Typography.Text>
                      </div>
                      <Progress
                        percent={Math.min(100, (skill.Progress / 6667) * 100)}
                        showInfo={false}
                        strokeColor="var(--dashboard-teal)"
                        trailColor="var(--dashboard-track)"
                      />
                    </div>
                  ))}
                  {!userSkills.length ? (
                    <Typography.Text>
                      No hay skills cargadas todavía.
                    </Typography.Text>
                  ) : null}
                </Space>
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
